import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import uniqueId from 'lodash/uniqueId'

// Components
import { Input, FieldArray } from 'components/form/inputs'
import { Button, IconCheck, IconCloseFat, IconPen, IconPlusAdd } from '@tracklib/kit'

import styles from './links.css'

export type Link = {
  id: string
  link: string
}

type LinkInputProps = {
  onReview: boolean
  index: number
  remove: (index: number) => void
  linksLength: number
  linkObj: Link
}

type LinksProps = {
  onReview: boolean
  links: Array<Link>
}

const expression = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.~#?&//=]*)?/gi
const regex = new RegExp(expression)

const validateLink = (value: string) => {
  let error
  if (!value) {
    error = 'A link is required'
  } else if (!value.match(regex)) {
    error = 'Invalid link address'
  }
  return error
}

const LinkInput = (props: LinkInputProps) => {
  const { onReview, index, remove, linksLength, linkObj } = props
  const [isEditing, setIsEditing] = useState(true)

  useEffect(() => {
    if (onReview) {
      setIsEditing(false)
    }
  }, [onReview])

  if (!isEditing) {
    return (
      <>
        <div className={styles.textData}>{linkObj.link}</div>
        <Button className={styles.btnEdit} variant="outline" onClick={() => setIsEditing(!isEditing)}>
          <IconPen />
        </Button>
      </>
    )
  }

  return (
    <>
      <Input
        className={styles.input}
        name={`links[${index}].link`}
        type="text"
        label={onReview ? '' : `Link ${index + 1}`}
        placeholder="http://open.spotify.com/playlist/6rxsFf4TYVzEze3wezhrLa"
        validate={validateLink}
      />
      {linksLength > 1 && (
        <Button className={styles.btnRemove} onClick={() => remove(index)}>
          <IconCloseFat />
        </Button>
      )}
      {onReview && (
        <Button className={styles.btnEditConfirm} onClick={() => setIsEditing(!isEditing)}>
          <IconCheck />
        </Button>
      )}
    </>
  )
}

const Links = (props: LinksProps) => {
  const { onReview, links } = props

  const linksLength = links.length

  const allowAddLink = () => {
    if (linksLength >= 1) {
      return links[linksLength - 1].link === '' || linksLength >= 5
    }
    return false
  }

  return (
    <FieldArray name="links">
      {({ push, remove }) => (
        <>
          <ul className={styles.linksList}>
            <AnimatePresence>
              {links.map((linkObj, index) => (
                <motion.li
                  className={styles.listItem}
                  key={linkObj.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <LinkInput onReview={onReview} index={index} remove={remove} linksLength={linksLength} linkObj={linkObj} />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          {linksLength < 5 && !onReview && (
            <Button
              className={styles.btnAdd}
              variant="outline"
              disabled={allowAddLink()}
              onClick={() =>
                push({
                  id: uniqueId(),
                  link: '',
                })
              }
            >
              <IconPlusAdd />
              <span>Add another link</span>
            </Button>
          )}
        </>
      )}
    </FieldArray>
  )
}

export default Links
